import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {FormService} from '../shared/services/form.service';
import {FormDefinition} from '../shared/models/form-schema.model';
import {BuilderStateService} from '../builder/services/builder-state.service';

import {FormContainerComponent} from "../../../_components/form-container/form-container.component";

@Component({
  selector: 'app-form-manager',
  standalone: true,
  imports: [CommonModule, FormsModule, FormContainerComponent, RouterLink],
  template: `
    <div class="px-4 sm:px-6 lg:px-8 h-full flex flex-col">
      <div class="sm:flex sm:items-center sm:justify-between">
        <div class="flex space-x-1">
          <h1 class="font-semibold leading-6 text-gray-900 !text-3xl pointer-events-none">
            Formulare
          </h1>
        </div>
        <a routerLink="submissions"
           class="mt-3 sm:mt-0 inline-flex items-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
          Eingaben ansehen
        </a>
      </div>

      <layout-form-container (clear)="newFormTitle = ''"
                             (submit)="createForm()"
                             [isCollapsed]="true"
                             [(searchValue)]="searchQuery"
                             clearText="Abbrechen"
                             submitText="Neues Formular">
        <div class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
          <div class="sm:col-span-6">
            <label class="block text-sm font-medium leading-6 text-gray-900" for="form-title">Titel</label>
            <input [(ngModel)]="newFormTitle"
                   class="mt-2 block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                   id="form-title" name="form-title"
                   placeholder="Mein Formular-Titel"
                   type="text">
          </div>
        </div>
      </layout-form-container>

      <div class="-mx-4 mt-8 sm:-mx-0 overflow-x-auto relative h-full overflow-y-auto">
        <table class="min-w-full divide-y divide-gray-300">
          <thead>
          <tr>
            <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0" scope="col">Titel</th>
            <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" scope="col">Schlüssel</th>
            <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" scope="col">Version</th>
            <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" scope="col">Status</th>
            <th class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" scope="col">Seiten</th>
            <th class="relative py-3.5 pl-3 pr-4 sm:pr-0" scope="col">
              <span class="sr-only">Aktionen</span>
            </th>
          </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
          <tr *ngFor="let form of filteredForms" class="group">
            <td (click)="editForm(form)"
                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-0 text-theme-primary-700 hover:text-theme-primary-900 cursor-pointer">
              {{ form.title }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
              {{ form.key }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              v{{ form.version }}
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm">
                <span
                  [class]="'px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider ' + getStatusClass(form.status)">
                  {{ form.status }}
                </span>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
              {{ form.pages.length }}
            </td>
            <td
              class="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0 inline-flex w-full justify-end space-x-3">
              <a (click)="editForm(form)"
                 class="cursor-pointer text-gray-600 hover:text-gray-800">
                <svg class="bi bi-pen" height="18" viewBox="0 0 16 16" width="18" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="m13.498.795.149-.149a1.207 1.207 0 1 1 1.707 1.708l-.149.148a1.5 1.5 0 0 1-.059 2.059L4.854 14.854a.5.5 0 0 1-.233.131l-4 1a.5.5 0 0 1-.606-.606l1-4a.5.5 0 0 1 .131-.232l9.642-9.642a.5.5 0 0 0-.642.056L6.854 4.854a.5.5 0 1 1-.708-.708L9.44.854A1.5 1.5 0 0 1 11.5.796a1.5 1.5 0 0 1 1.998-.001zm-.644.766a.5.5 0 0 0-.707 0L1.95 11.756l-.764 3.057 3.057-.764L14.44 3.854a.5.5 0 0 0 0-.708l-1.585-1.585z"/>
                </svg>
              </a>
              <a (click)="openRequirements(form, $event)" title="Pflicht-Zuweisung"
                 class="cursor-pointer text-gray-600 hover:text-gray-800">
                <svg fill="none" height="18" stroke="currentColor" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
                </svg>
              </a>
              <a (click)="deleteForm(form, $event)"
                 class="cursor-pointer text-red-600 hover:text-red-900">
                <svg class="bi bi-trash3" height="18" viewBox="0 0 16 16" width="18" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z"/>
                </svg>
              </a>
            </td>
          </tr>
          </tbody>
        </table>

        <div *ngIf="filteredForms.length === 0" class="py-12 text-center">
          <p class="text-gray-500">Keine Formulare gefunden.</p>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class FormManagerComponent implements OnInit {
  forms: FormDefinition[] = [];
  searchQuery: string = '';
  newFormTitle: string = '';

  constructor(
    private formService: FormService,
    private builderState: BuilderStateService,
    private router: Router
  ) {
  }

  get filteredForms() {
    if (!this.searchQuery) return this.forms;
    const query = this.searchQuery.toLowerCase();
    return this.forms.filter(f =>
      f.title.toLowerCase().includes(query) ||
      f.key.toLowerCase().includes(query)
    );
  }

  ngOnInit() {
    this.formService.getForms().subscribe(forms => {
      this.forms = forms;
    });
  }

  getStatusClass(status: string) {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-700';
      case 'draft':
        return 'bg-orange-100 text-orange-700';
      case 'archived':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  createForm() {
    if (!this.newFormTitle) return;
    this.formService.addForm(this.newFormTitle).subscribe(newForm => {
      this.newFormTitle = '';
      this.editForm(newForm);
    });
  }

  editForm(form: FormDefinition) {
    this.builderState.loadDefinition(JSON.parse(JSON.stringify(form)));
    this.router.navigate(['/company/forms/builder'], {queryParams: {id: form.id}});
  }

  /** Open the "required for…" assignment page for this form. */
  openRequirements(form: FormDefinition, event: MouseEvent) {
    event.stopPropagation();
    this.router.navigate(['/company/forms/requirements'], {queryParams: {id: form.id}});
  }

  deleteForm(form: FormDefinition, event: MouseEvent) {
    event.stopPropagation();
    if (confirm(`Sind Sie sicher, dass Sie das Formular "${form.title}" löschen möchten?`)) {
      this.formService.deleteForm(form.id).subscribe();
    }
  }
}
